import React, { useState, useEffect } from 'react';
import { FiCalendar, FiFilter, FiSearch, FiTruck, FiUser, FiDollarSign, FiCheck, FiX, FiImage } from 'react-icons/fi';
import { AdminLayout } from '../../components/layout';
import { Card, Loading, Button, AlertModal, ConfirmDialog } from '../../components/common';
import { getAllBookings, updateBooking, cancelBooking } from '../../firebase/bookings';
import { getAllCompanies } from '../../firebase/companies';
import BookingImageGallery from '../../components/BookingImageGallery';

const AdminBookingList = () => {
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [alert, setAlert] = useState({ isOpen: false, type: 'success', title: '', message: '' });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, message: '', onConfirm: null, title: '', type: 'warning' });
  const [cancelReason, setCancelReason] = useState('');
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [bookingsResult, companiesResult] = await Promise.all([
        getAllBookings(),
        getAllCompanies()
      ]);
      
      if (bookingsResult.bookings) {
        setBookings(bookingsResult.bookings);
      }
      if (companiesResult.companies) {
        setCompanies(companiesResult.companies);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get company name by ID
  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company?.name || 'Unknown';
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Asia/Kolkata'
      });
    } catch {
      return dateString;
    }
  };

  // Filter bookings
  const filteredBookings = bookings.filter(booking => {
    const matchesSearch = 
      booking.customerDetails?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.customerDetails?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.carDetails?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCompany = selectedCompany === 'all' || booking.companyId === selectedCompany;
    const matchesStatus = selectedStatus === 'all' || booking.status === selectedStatus;
    
    return matchesSearch && matchesCompany && matchesStatus;
  });

  // Calculate totals
  const totalRevenue = filteredBookings.reduce((sum, b) => sum + (b.totalAmount || 0), 0);

  // Handle status update
  const handleStatusUpdate = (bookingId, newStatus) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Change Booking Status',
      message: `Are you sure you want to change the booking status to ${newStatus}?`,
      type: 'warning',
      onConfirm: async () => {
        try {
          const result = await updateBooking(bookingId, { status: newStatus });
          if (result.error) {
            setAlert({
              isOpen: true,
              type: 'error',
              title: 'Update Failed',
              message: result.error || 'Failed to update status. Please try again.'
            });
          } else {
            setAlert({
              isOpen: true,
              type: 'success',
              title: 'Status Updated',
              message: `Booking status has been changed to ${newStatus}.`
            });
            loadData();
          }
        } catch (error) {
          console.error('Error updating booking:', error);
          setAlert({
            isOpen: true,
            type: 'error',
            title: 'Update Failed',
            message: 'An error occurred while updating the booking status.'
          });
        }
        setConfirmDialog({ ...confirmDialog, isOpen: false });
      }
    });
  };

  // Handle cancel booking
  const handleCancelBooking = (booking) => {
    setBookingToCancel(booking);
    setShowCancelDialog(true);
  };

  const confirmCancelBooking = async () => {
    if (!bookingToCancel) return;
    
    try {
      const result = await cancelBooking(bookingToCancel.id, cancelReason || 'No reason provided', 'admin');
      if (result.error) {
        setAlert({
          isOpen: true,
          type: 'error',
          title: 'Cancellation Failed',
          message: result.error || 'Failed to cancel booking. Please try again.'
        });
      } else {
        setAlert({
          isOpen: true,
          type: 'success',
          title: 'Booking Cancelled',
          message: 'The booking has been cancelled successfully.'
        });
        loadData();
      }
    } catch (error) {
      console.error('Error cancelling booking:', error);
      setAlert({
        isOpen: true,
        type: 'error',
        title: 'Cancellation Failed',
        message: 'An error occurred while cancelling the booking.'
      });
    }
    
    setShowCancelDialog(false);
    setBookingToCancel(null);
    setCancelReason('');
  };

  if (loading) {
    return (
      <AdminLayout title="All Bookings">
        <div className="flex items-center justify-center h-64">
          <Loading />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="All Bookings">
      <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">All Bookings</h1>
          <p className="text-gray-600">
            {filteredBookings.length} booking{filteredBookings.length !== 1 ? 's' : ''} • 
            Total Revenue: ₹{totalRevenue.toLocaleString()}
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <FiCalendar className="text-blue-600" size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{bookings.length}</p>
              <p className="text-sm text-gray-500">Total Bookings</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <FiCalendar className="text-green-600" size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {bookings.filter(b => b.status === 'confirmed').length}
              </p>
              <p className="text-sm text-gray-500">Confirmed</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <FiCalendar className="text-red-600" size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {bookings.filter(b => b.status === 'cancelled').length}
              </p>
              <p className="text-sm text-gray-500">Cancelled</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
              <FiCalendar className="text-yellow-600" size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {bookings.filter(b => b.status === 'pending').length}
              </p>
              <p className="text-sm text-gray-500">Pending</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <FiDollarSign className="text-amber-600" size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">₹{totalRevenue.toLocaleString()}</p>
              <p className="text-sm text-gray-500">Total Revenue</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by customer name, email or car..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Company Filter */}
          <div className="flex items-center gap-2">
            <FiFilter className="text-gray-400" />
            <select
              value={selectedCompany}
              onChange={(e) => setSelectedCompany(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">All Companies</option>
              {companies.map(company => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </Card>

      {/* Bookings List */}
      {filteredBookings.length === 0 ? (
        <Card className="p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FiCalendar className="text-gray-400" size={32} />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No bookings found</h3>
          <p className="text-gray-600">
            {searchTerm || selectedCompany !== 'all' || selectedStatus !== 'all'
              ? 'Try adjusting your filters'
              : 'No bookings have been made yet'}
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredBookings.map(booking => (
            <Card key={booking.id} className="p-4">
              <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                {/* Car Image */}
                <div className="w-full lg:w-24 h-20 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                  {booking.carDetails?.image || booking.carDetails?.images?.[0] ? (
                    <img 
                      src={booking.carDetails.image || booking.carDetails.images?.[0]} 
                      alt={booking.carDetails?.name}
                      className="w-full h-full object-contain bg-gray-100"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <FiTruck className="text-gray-400" size={24} />
                    </div>
                  )}
                </div>

                {/* Booking Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {booking.carDetails?.name || 'Unknown Car'}
                      </h3>
                      <p className="text-sm text-gray-600 flex items-center gap-1">
                        <FiUser size={12} />
                        {booking.customerDetails?.name || booking.customerDetails?.email || 'Unknown'}
                      </p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      booking.status === 'confirmed' 
                        ? 'bg-green-100 text-green-700'
                        : booking.status === 'completed'
                        ? 'bg-blue-100 text-blue-700'
                        : booking.status === 'cancelled'
                        ? 'bg-red-100 text-red-700'
                        : booking.status === 'pending'
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {booking.status}
                    </span>
                  </div>
                  
                  <div className="mt-2 flex flex-wrap gap-4 text-sm text-gray-600">
                    <span>
                      📅 {formatDate(booking.fromDateTime)}
                    </span>
                    <span>→</span>
                    <span>
                      📅 {formatDate(booking.toDateTime)}
                    </span>
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                      {getCompanyName(booking.companyId)}
                    </span>
                  </div>

                  {/* Customer Details */}
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <h4 className="text-sm font-semibold text-gray-900 mb-2">Customer Details</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
                      <div>
                        <span className="font-medium">Name:</span> {booking.customerDetails?.name || 'N/A'}
                      </div>
                      <div>
                        <span className="font-medium">Email:</span> {booking.customerDetails?.email || 'N/A'}
                      </div>
                      <div>
                        <span className="font-medium">Phone:</span> {booking.customerDetails?.phone || 'N/A'}
                      </div>
                      {booking.customerDetails?.alternatePhone && (
                        <div>
                          <span className="font-medium">Alternate Phone:</span> {booking.customerDetails.alternatePhone}
                        </div>
                      )}
                      {booking.customerDetails?.whatsappNumber && (
                        <div>
                          <span className="font-medium">WhatsApp:</span> {booking.customerDetails.whatsappNumber}
                        </div>
                      )}
                      {booking.customerDetails?.aadharNumber && (
                        <div>
                          <span className="font-medium">Aadhar:</span> {booking.customerDetails.aadharNumber.replace(/(\d{4})\s?(\d{4})\s?(\d{4})/, 'XXXX XXXX $3')}
                        </div>
                      )}
                      {booking.customerDetails?.address && (
                        <div className="md:col-span-2">
                          <span className="font-medium">Address:</span> {booking.customerDetails.address}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Signature */}
                  {booking.signature?.image && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Digital Signature</h4>
                      <div className="relative inline-block">
                        <img
                          src={booking.signature.image}
                          alt="Customer Signature"
                          className="h-24 border border-gray-300 rounded-lg bg-white p-2"
                        />
                        <button
                          onClick={() => {
                            const modal = window.open('', '_blank', 'width=600,height=400');
                            modal.document.write(`
                              <html>
                                <head><title>Customer Signature</title></head>
                                <body style="margin:0;padding:20px;display:flex;justify-content:center;align-items:center;background:#f3f4f6;">
                                  <img src="${booking.signature.image}" style="max-width:100%;max-height:100%;border:1px solid #d1d5db;border-radius:8px;background:white;padding:10px;" />
                                </body>
                              </html>
                            `);
                          }}
                          className="ml-2 text-xs text-primary-600 hover:text-primary-700 underline"
                        >
                          View Full Size
                        </button>
                      </div>
                      {booking.signature.signedAt && (
                        <p className="text-xs text-gray-500 mt-1">
                          Signed: {new Date(booking.signature.signedAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Car Images Section */}
                  {(booking.images?.beforeRide?.length > 0 || booking.images?.afterRide?.length > 0) && (
                    <div className="pt-4 border-t border-gray-200 mt-4">
                      <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-3">
                        <FiImage size={16} />
                        Car Images
                      </h4>

                      {/* Before Ride Images */}
                      {booking.images?.beforeRide && booking.images.beforeRide.length > 0 && (
                        <div className="mb-4">
                          <BookingImageGallery
                            images={booking.images.beforeRide}
                            imageType="before-ride"
                            currentUserId={null}
                            onDelete={null}
                            canDelete={false}
                          />
                        </div>
                      )}

                      {/* After Ride Images */}
                      {booking.images?.afterRide && booking.images.afterRide.length > 0 && (
                        <div className="mb-4">
                          <BookingImageGallery
                            images={booking.images.afterRide}
                            imageType="after-ride"
                            currentUserId={null}
                            onDelete={null}
                            canDelete={false}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Amount */}
                <div className="text-right">
                  <p className="text-xl font-bold text-gray-900">
                    ₹{booking.totalAmount?.toLocaleString() || 0}
                  </p>
                  <p className="text-sm text-gray-500">
                    {booking.duration || `${booking.durationHours || 0}h`}
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-4 pt-4 border-t border-gray-200 flex flex-wrap gap-2">
                {booking.status === 'pending' && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleStatusUpdate(booking.id, 'confirmed')}
                      className="flex items-center gap-2"
                    >
                      <FiCheck size={14} />
                      Confirm
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleCancelBooking(booking)}
                      className="flex items-center gap-2 text-red-600 hover:text-red-700"
                    >
                      <FiX size={14} />
                      Cancel
                    </Button>
                  </>
                )}
                {booking.status === 'confirmed' && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleStatusUpdate(booking.id, 'completed')}
                      className="flex items-center gap-2"
                    >
                      <FiCheck size={14} />
                      Mark Completed
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleCancelBooking(booking)}
                      className="flex items-center gap-2 text-red-600 hover:text-red-700"
                    >
                      <FiX size={14} />
                      Cancel
                    </Button>
                  </>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
      </div>

      {/* Alert Modal */}
      <AlertModal
        isOpen={alert.isOpen}
        onClose={() => setAlert({ ...alert, isOpen: false })}
        type={alert.type}
        title={alert.title}
        message={alert.message}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
      />

      {/* Cancel Booking Dialog */}
      {showCancelDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Cancel Booking</h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to cancel this booking? This action cannot be undone.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cancellation Reason (Optional)
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Enter reason for cancellation..."
              />
            </div>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCancelDialog(false);
                  setBookingToCancel(null);
                  setCancelReason('');
                }}
              >
                Keep Booking
              </Button>
              <Button
                variant="danger"
                onClick={confirmCancelBooking}
              >
                Cancel Booking
              </Button>
            </div>
          </Card>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminBookingList;
